<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { Search, Users } from '@lucide/vue';
import {
  CategoriaContatto,
  type CategoriaContatto as TCategoriaContatto,
  type Contatto,
} from '@evolfin/shared';
import ContattoLink from '@/components/ui/ContattoLink.vue';
import LinkScheda from '@/components/ui/LinkScheda.vue';
import StatoVuoto from '@/components/ui/StatoVuoto.vue';
import AvatarContatto from '../components/AvatarContatto.vue';
import { listaRubrica } from '../api';

definePage({ meta: { titolo: 'Rubrica' } });

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const testoRicerca = ref('');
const qRicerca = ref('');
const categoriaFiltro = ref<TCategoriaContatto | ''>('');

let timerRicerca: ReturnType<typeof setTimeout> | undefined;
function cerca(testo: string): void {
  clearTimeout(timerRicerca);
  timerRicerca = setTimeout(() => {
    qRicerca.value = testo.trim();
  }, 350);
}

function alternaCategoria(c: TCategoriaContatto): void {
  categoriaFiltro.value = categoriaFiltro.value === c ? '' : c;
}

const { data, isFetching } = useQuery({
  queryKey: computed(() => ['rubrica', { q: qRicerca.value, categoria: categoriaFiltro.value }]),
  queryFn: () =>
    listaRubrica({
      q: qRicerca.value || undefined,
      categoria: categoriaFiltro.value || undefined,
    }),
  placeholderData: (precedente) => precedente,
});

const contatti = computed(() => data.value ?? []);

/** Raggruppa per la prima lettera di `denominazione` (già "Cognome Nome" per
 * le persone): l'elenco arriva già ordinato dal backend, si riordina qui solo
 * in via difensiva. */
const gruppi = computed(() => {
  const ordinati = [...contatti.value].sort((a, b) =>
    a.denominazione.localeCompare(b.denominazione, 'it'),
  );
  const mappa = new Map<string, Contatto[]>();
  for (const c of ordinati) {
    const lettera = c.denominazione.trim().charAt(0).toLocaleUpperCase('it') || '#';
    if (!mappa.has(lettera)) mappa.set(lettera, []);
    mappa.get(lettera)!.push(c);
  }
  return [...mappa.entries()].map(([lettera, righe]) => ({ lettera, righe }));
});

const lettereDisponibili = computed(() => new Set(gruppi.value.map((g) => g.lettera)));

const sezioni: Record<string, HTMLElement> = {};
function registraSezione(lettera: string, el: unknown): void {
  if (el instanceof HTMLElement) sezioni[lettera] = el;
}
function vaiA(lettera: string): void {
  sezioni[lettera]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Le persone non hanno una scheda propria: si apre quella del contenitore sulle persone. */
function schedaDi(c: Contatto): string | null {
  switch (c.categoria) {
    case 'Cliente':
      return `/clienti/${c.id}`;
    case 'Partner':
      return `/partner/${c.id}`;
    case 'Persona cliente':
      return c.appartenenzaId ? `/clienti/${c.appartenenzaId}?tab=persone` : null;
    case 'Persona partner':
      return c.appartenenzaId ? `/partner/${c.appartenenzaId}?tab=persone` : null;
    case 'Utente':
      return null;
  }
}

const coloriCategoria: Record<TCategoriaContatto, string> = {
  Cliente: 'var(--color-info)',
  'Persona cliente': 'var(--color-info)',
  Partner: 'var(--color-accent)',
  'Persona partner': 'var(--color-accent)',
  Utente: 'var(--color-success)',
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <header>
      <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Anagrafiche</p>
      <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Rubrica</h1>
      <p class="mt-1 text-[13px] text-ink-secondary">
        Tutti i contatti in un unico elenco: clienti, partner e le loro persone, staff Evolfin.
      </p>
    </header>

    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center gap-3">
        <label class="relative min-w-[14rem] flex-1">
          <Search
            class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            :stroke-width="2"
            aria-hidden="true"
          />
          <input
            v-model="testoRicerca"
            type="search"
            placeholder="Cerca per nome, email, azienda…"
            class="w-full rounded-sm border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-ink transition-[border-color] focus:border-[var(--color-accent)]"
            @input="cerca(testoRicerca)"
          />
        </label>

        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="c in CategoriaContatto.options"
            :key="c"
            type="button"
            class="pill-categoria"
            :class="{ attiva: categoriaFiltro === c }"
            @click="alternaCategoria(c)"
          >
            {{ c }}
          </button>
        </div>
      </div>

      <p class="text-[12.5px] text-ink-muted">
        {{ contatti.length }} contatt{{ contatti.length === 1 ? 'o' : 'i' }}
      </p>
    </div>

    <div class="relative flex items-start gap-3">
      <div v-if="isFetching" class="barra-caricamento" role="status" aria-label="Caricamento" />

      <div
        class="min-w-0 flex-1"
        :class="isFetching && 'opacity-70 transition-opacity duration-150'"
      >
        <StatoVuoto
          v-if="!gruppi.length"
          :icona="Users"
          titolo="Nessun contatto trovato"
          descrizione="Prova a cambiare il testo di ricerca o la categoria selezionata."
        />

        <div v-else class="flex flex-col gap-6">
          <section
            v-for="gruppo in gruppi"
            :key="gruppo.lettera"
            :ref="(el) => registraSezione(gruppo.lettera, el)"
          >
            <p class="lettera-separatore">{{ gruppo.lettera }}</p>
            <div class="flex flex-col gap-2">
              <div v-for="c in gruppo.righe" :key="`${c.categoria}-${c.id}`" class="riga-contatto">
                <AvatarContatto :contatto="c" />

                <div class="min-w-0 flex-1">
                  <p class="truncate font-medium text-ink">
                    <LinkScheda v-if="schedaDi(c)" :a="schedaDi(c)!" nuova-scheda>{{
                      c.denominazione
                    }}</LinkScheda>
                    <template v-else>{{ c.denominazione }}</template>
                  </p>
                  <p v-if="c.ruolo || c.appartenenza" class="truncate text-[12px] text-ink-muted">
                    {{ c.ruolo }}<span v-if="c.ruolo && c.appartenenza"> · </span
                    >{{ c.appartenenza }}
                  </p>
                </div>

                <span
                  class="shrink-0 rounded-pill px-2 py-0.5 text-[11px] font-medium"
                  :style="{
                    background: `color-mix(in oklch, ${coloriCategoria[c.categoria]} 12%, transparent)`,
                    color: coloriCategoria[c.categoria],
                  }"
                >
                  {{ c.categoria }}
                </span>

                <div class="hidden w-56 shrink-0 lg:block">
                  <ContattoLink v-if="c.email" :valore="c.email" tipo="email" />
                  <span v-else class="text-[12px] text-ink-muted">—</span>
                </div>
                <div class="hidden w-40 shrink-0 lg:block">
                  <ContattoLink v-if="c.telefono" :valore="c.telefono" tipo="telefono" />
                  <span v-else class="text-[12px] text-ink-muted">—</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <nav class="nav-rapida sticky top-20 hidden shrink-0 lg:flex" aria-label="Vai alla lettera">
        <button
          v-for="l in ALFABETO"
          :key="l"
          type="button"
          :disabled="!lettereDisponibili.has(l)"
          @click="vaiA(l)"
        >
          {{ l }}
        </button>
      </nav>
    </div>
  </div>
</template>

<style scoped>
.pill-categoria {
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border);
  background-color: var(--color-surface);
  padding: 0.4rem 0.85rem;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--color-text-secondary);
  transition:
    border-color var(--transition-fast),
    background-color var(--transition-fast),
    color var(--transition-fast);
}
.pill-categoria:hover {
  border-color: var(--color-accent);
}
.pill-categoria.attiva {
  border-color: var(--color-accent);
  background: var(--surface-accent-soft);
  color: var(--color-primary);
}

.lettera-separatore {
  margin-bottom: 0.5rem;
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.riga-contatto {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  padding: 0.65rem 1rem;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}
.riga-contatto:hover {
  border-color: var(--color-border);
  box-shadow: var(--shadow-card);
}

.nav-rapida {
  flex-direction: column;
  gap: 1px;
  padding: 0.4rem 0.25rem;
}
.nav-rapida button {
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--color-text-muted);
  transition:
    background-color var(--transition-fast),
    color var(--transition-fast);
}
.nav-rapida button:not(:disabled):hover {
  background: var(--surface-accent-soft);
  color: var(--color-primary);
}
.nav-rapida button:disabled {
  color: var(--color-border);
  cursor: default;
}

.barra-caricamento {
  position: absolute;
  inset-inline: 0;
  top: 0;
  z-index: 2;
  height: 2px;
  overflow: hidden;
  border-radius: 999px;
  background: color-mix(in oklch, var(--color-accent) 18%, transparent);
}
.barra-caricamento::after {
  content: '';
  position: absolute;
  inset-block: 0;
  inline-size: 40%;
  border-radius: 999px;
  background: var(--color-accent);
  animation: scorri-barra 1.1s ease-in-out infinite;
}
@keyframes scorri-barra {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .barra-caricamento::after {
    animation: none;
    inline-size: 100%;
    opacity: 0.6;
  }
}
</style>
